import { useState } from "react";
import { IAllLabRequisitionItem } from "@home-health-notify/models";
import {
  Chip,
  Collapse,
  IconButton,
  Link,
  TableRow,
  Typography,
} from "@mui/material";
import { ChevronRight, ExpandMore } from "@mui/icons-material";
import { useNavigate } from "react-router";
import { ListingTableCell, PatientName } from "../common/ListingTable.style";
import { ROUTES } from "../routes";
import {
  Label,
  OrderContainer,
  OrderId,
  OrderIdContainer,
  StatusChip,
} from "../chatInfo/tabs/labs/LabOrderListItem.style";
import {
  ExpandedContent,
  OrderHeaderRow,
  OrdersList,
  SectionContent,
  SectionTitle,
  SpecimenInfoContainer,
} from "./LabRequisitionsTableItem.style";
import LabOrderReports from "./LabOrderReports";

type LabRequisitionStatus = "PENDING" | "ORDER_PLACED" | "FAILED";

const statusChipColor: Record<
  LabRequisitionStatus,
  "info" | "success" | "error"
> = {
  PENDING: "info",
  ORDER_PLACED: "success",
  FAILED: "error",
};

const statusLabel: Record<LabRequisitionStatus, string> = {
  PENDING: "Pending",
  ORDER_PLACED: "Order Placed",
  FAILED: "Failed",
};

const getStatusColor = (status: string) => {
  switch (status) {
    case "ACCEPTED":
      return "info";
    case "COMPLETED":
      return "success";
    case "FAILED":
      return "error";
    default:
      return "default";
  }
};

function formatDateTime(isoString: string) {
  const date = new Date(isoString);
  const dateStr = date.toLocaleDateString("en-US", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
  const timeStr = date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
  });
  return `${dateStr} ${timeStr}`;
}

const LabRequisitionsTableItem = ({
  requisition,
  number,
  orgId,
}: {
  requisition: IAllLabRequisitionItem;
  number: number;
  orgId: string;
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const navigate = useNavigate();
  const status = requisition.status as LabRequisitionStatus;

  const testsDisplay = requisition.tests.map(t => t.name).join(", ");

  const externalOrderIds =
    requisition.labOrders.length > 0
      ? requisition.labOrders.map(o => o.externalLabOrderId).join(", ")
      : "—";

  const handlePatientClick = () => {
    navigate(
      `${ROUTES.ORGANIZATIONS}/${orgId}/${ROUTES.CHATS}/${ROUTES.PATIENT}/${requisition.patientId}/${ROUTES.MESSAGE}`,
    );
  };

  const { specimenInformation } = requisition;

  return (
    <>
      <TableRow
        sx={{
          "&:last-child td, &:last-child th": { border: 0 },
          cursor: "pointer",
        }}
        data-testid="lab-requisition-table-item"
        onClick={() => setIsExpanded(prev => !prev)}
      >
        <ListingTableCell>
          <IconButton size="small" data-testid="expand-button">
            {isExpanded ? (
              <ExpandMore fontSize="small" />
            ) : (
              <ChevronRight fontSize="small" />
            )}
          </IconButton>
        </ListingTableCell>
        <ListingTableCell data-testid="serial-number">
          {number}
        </ListingTableCell>
        <ListingTableCell data-testid="patient-name">
          <PatientName noWrap>
            <Link
              component="button"
              underline="hover"
              onClick={e => {
                e.stopPropagation();
                handlePatientClick();
              }}
              sx={{ textTransform: "capitalize", textAlign: "left" }}
            >
              {requisition.patientName}
            </Link>
          </PatientName>
        </ListingTableCell>
        <ListingTableCell data-testid="lab-name">
          {requisition.labName}
        </ListingTableCell>
        <ListingTableCell data-testid="tests">{testsDisplay}</ListingTableCell>
        <ListingTableCell data-testid="external-order-ids">
          {externalOrderIds}
        </ListingTableCell>
        <ListingTableCell data-testid="status">
          <Chip
            label={statusLabel[status]}
            color={statusChipColor[status]}
            size="small"
          />
        </ListingTableCell>
        <ListingTableCell data-testid="created-date">
          {formatDateTime(requisition.createdAt)}
        </ListingTableCell>
      </TableRow>
      <TableRow>
        <ListingTableCell
          colSpan={8}
          sx={{
            paddingTop: 0,
            paddingBottom: 0,
            border: isExpanded ? undefined : "none",
          }}
        >
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <ExpandedContent>
              <SectionTitle>Tests</SectionTitle>
              <SectionContent>
                {requisition.tests
                  .map(
                    t => `${t.name}${t.panelType ? ` (${t.panelType})` : ""}`,
                  )
                  .join(", ")}
              </SectionContent>

              <SectionTitle>Specimen Information</SectionTitle>
              <SpecimenInfoContainer>
                <OrderHeaderRow>
                  <Label>Collection Date:</Label>
                  <Typography variant="body2">
                    {specimenInformation.collectionDate}
                    {specimenInformation.collectionTime &&
                      ` at ${specimenInformation.collectionTime}`}
                    {specimenInformation.collectionTimeZone &&
                      ` (${specimenInformation.collectionTimeZone})`}
                  </Typography>
                </OrderHeaderRow>
                <OrderHeaderRow>
                  <Label>Type:</Label>
                  <Typography variant="body2">
                    {specimenInformation.requisitionType}
                  </Typography>
                </OrderHeaderRow>
              </SpecimenInfoContainer>

              <SectionTitle>Lab Orders</SectionTitle>
              {requisition.labOrders.length > 0 ? (
                <OrdersList>
                  {requisition.labOrders.map(order => (
                    <OrderContainer key={order.id}>
                      <OrderHeaderRow>
                        <OrderIdContainer>
                          <Label>Order Id:</Label>
                          <OrderId variant="body2">
                            {order.externalLabOrderId}
                          </OrderId>
                        </OrderIdContainer>
                        <StatusChip
                          variant="outlined"
                          label={order.status}
                          size="small"
                          color={getStatusColor(order.status) as any}
                        />
                      </OrderHeaderRow>
                      <LabOrderReports reports={order.reports} />
                    </OrderContainer>
                  ))}
                </OrdersList>
              ) : (
                <Typography variant="body2">No lab orders</Typography>
              )}
            </ExpandedContent>
          </Collapse>
        </ListingTableCell>
      </TableRow>
    </>
  );
};

export default LabRequisitionsTableItem;
